import { useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Box, Button, FormField, FormHelp, Input, Label } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_email } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useScreenSize } from '../../../hooks';
import { getListingDetailsQueryKey, useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';

import { VERIFICATION_STEPS } from '../../modals/VerificationModal/constants';

import { VerificationModal } from '../../modals/VerificationModal';

import { useAuth } from '../../AuthProvider';
import ContactEmailMismatchAlert from '../../ContactEmailMismatchAlert';
import ContactEmailDeliverabilityAlert from '../ContactEmailDeliverabilityAlert';

// Email verification skips the GET_VERIFICATION_CODE step (code is sent automatically)
const EMAIL_VERIFICATION_CONFIG = [
  VERIFICATION_STEPS.ADD_CONTENT,
  VERIFICATION_STEPS.VERIFICATION_CODE_SENT,
  VERIFICATION_STEPS.VERIFICATION_SUCCESS,
];

type Props = {
  onClose?: () => void;
};

const EmailFormField = ({ onClose }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { isScreenDesktop } = useScreenSize();
  const { user } = useAuth();
  const { mode } = useFormMode();
  const queryClient = useQueryClient();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isInModalState = mode === 'modal';
  const contactEmail = listingDetails?.email ?? '';
  const isContactEmailVerified = listingDetails?.isContactEmailVerified ?? false;
  const canViewContactEmailDeliverability = user?.features?.canViewContactEmailDeliverability ?? false;

  const [showVerificationModal, setShowVerificationModal] = useState(Boolean(onClose));

  const updateMutation = useRentalManagerUserPropertyEditV2WebApiHandler_email({});

  const field = useFieldSave({
    fieldName: 'contactEmail',
    initialValue: contactEmail,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { listingAlias, email: value },
    }),
  });

  const handleEmailSave = async (value: string) => {
    field.onChange(value);
    await field.saveAsync().catch((err) => {
      throw {
        code: DatarouterError.getDisplayCode(err),
        message: DatarouterError.getDisplayMessage(err),
      };
    });

    // Await refetch to ensure data is fresh before modal advances to success step
    await queryClient.refetchQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
  };

  const handleClose = (value: boolean) => {
    if (!value) {
      onClose?.();
    }
    setShowVerificationModal(value);
  };

  const renderVerificationDate = () => {
    const dateToDisplay = listingDetails?.emailVerifiedOnMs || (isContactEmailVerified ? 'previously-verified' : '');
    if (!dateToDisplay) return null;

    const parsed = new Date(dateToDisplay);
    return (
      <FormHelp id="email-verification">
        {isValid(parsed) ? `Verified on ${format(parsed, 'MMMM d, yyyy')}` : 'Previously verified'}
      </FormHelp>
    );
  };

  const displayError = !!field.error && field.error !== 'Email verification pending';

  return (
    <>
      <FormField
        data-testid="EmailFormField"
        required={true}
        label={<Label>Email</Label>}
        control={
          contactEmail ? (
            <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
              <Input
                fluid
                readOnly={true}
                size={isScreenDesktop ? 'lg' : 'md'}
                name="contactEmail"
                value={contactEmail}
                aria-describedby="email-verification"
                className="fs-mask"
              />
              <Button tone="brand" emphasis="outlined" onClick={() => setShowVerificationModal(true)}>
                Edit
              </Button>
            </Box>
          ) : (
            <Button tone="brand" emphasis="outlined" onClick={() => setShowVerificationModal(true)}>
              Add email address
            </Button>
          )
        }
        error={displayError}
        description={
          <>
            {displayError ? (
              <FormHelp>{field.error}</FormHelp>
            ) : (
              <>
                {Boolean(field.value) && <FormHelp id="verification">{renderVerificationDate()}</FormHelp>}

                {!isInModalState && field.value && (
                  <Box css={{ marginBlock: '400' }}>
                    <ContactEmailMismatchAlert contactEmail={contactEmail} />
                  </Box>
                )}
              </>
            )}

            {canViewContactEmailDeliverability && !isInModalState && (
              <Box css={{ marginBlock: '400' }}>
                <ContactEmailDeliverabilityAlert />
              </Box>
            )}
          </>
        }
      />

      {showVerificationModal && (
        <VerificationModal
          headerText="Add and verify email address"
          onContentValueAdded={handleEmailSave}
          onVerificationConfirmation={handleEmailSave}
          showVerificationModal={handleClose}
          stepsConfig={EMAIL_VERIFICATION_CONFIG}
          verificationSource={PhoneVerificationSource.LISTING_CONTACT}
          verificationMode="email"
          listingDetails={listingDetails}
        />
      )}
    </>
  );
};

export default EmailFormField;
